import { useState } from 'react';
import { Button, InlineError, Input, Textarea } from '../../components/ui';
import { useMimic } from '../supplierOrganizations/MimicMode';
import {
  supplierOrderErrorMessage,
  useConfirmSupplierOrder,
  useRequestOrderRevision,
  useSetShipment,
} from './supplierOrders';
import type { JobOrder } from './jobOrder';

/**
 * The one thing this job needs from the factory, on the job's own page.
 *
 * These lived on the list card, which meant the decision and the evidence were
 * on different screens: a factory pressed Accept from a summary row, having
 * never seen the spec or the artwork. Moving them here is most of what makes
 * the page self-contained — the button now sits underneath the thing it is a
 * decision about.
 *
 * Exactly ONE action is offered at a time, chosen from the job's state. A page
 * showing Accept and Mark Ready together invites the wrong one, and the
 * database would refuse it with an error that reads like a fault.
 */
export function JobOrderActions({
  job,
  organizationId,
  onDone,
}: {
  job: JobOrder;
  organizationId: string;
  onDone: () => void;
}) {
  const { canWrite } = useMimic();
  const confirm = useConfirmSupplierOrder(organizationId);
  const revise = useRequestOrderRevision(organizationId);
  const shipment = useSetShipment(organizationId);

  const [error, setError] = useState('');
  const [revising, setRevising] = useState(false);
  const [note, setNote] = useState('');
  const [shipping, setShipping] = useState(false);
  const [tracking, setTracking] = useState(job.tracking_number ?? '');
  const [courier, setCourier] = useState(job.courier_name);
  const [trackingUrl, setTrackingUrl] = useState(job.tracking_url);
  const [shipNote, setShipNote] = useState('');

  const report = (cause: unknown) => {
    setError(supplierOrderErrorMessage(cause instanceof Error ? cause.message : ''));
  };

  // Read-only preview does NOT hide the buttons.
  //
  // It used to replace this whole section with one line of grey text, which
  // put "there is nothing to do on this job" and "the thing to do is hidden
  // from you" in the same visual place. Kyu hit it twice in ten minutes on a
  // job whose only outstanding action was Accept, and read it as a missing
  // feature both times — correctly, from what was on screen.
  //
  // The buttons render, disabled, with the reason stated above them. What the
  // factory would press is visible; only pressing it is withheld.
  const readOnly = !canWrite;

  const awaitingAcceptance = job.status === 'PO Sent to ES' && job.supplier_confirmed_at === null;
  const canMarkReady = job.status === 'In Production' && !job.supplier_ready_for_shipment;

  if (!awaitingAcceptance && !canMarkReady) {
    // Nothing to press. Said plainly rather than left as an empty box, because
    // an empty action area reads as something failing to load.
    return null;
  }

  return (
    <section className="job-order-section job-order-actions">
      <h4>What we need from you</h4>
      {error === '' ? null : <InlineError>{error}</InlineError>}
      {!readOnly ? null : (
        <p className="job-order-readonly">
          Preview only — you are viewing this portal as the supplier. Turn on{' '}
          <strong>Enable test writing</strong> in the Mimic Mode bar to press these.
        </p>
      )}

      {!awaitingAcceptance ? null : revising ? (
        <div>
          <Textarea
            label="What needs changing?"
            rows={3}
            placeholder="The target date is not possible for this quantity — earliest is…"
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />
          <div className="supplier-order-actions">
            <Button
              disabled={readOnly || revise.isPending}
              onClick={() => {
                setError('');
                revise.mutate(
                  { orderId: job.order_id, note },
                  {
                    onSuccess: () => {
                      setRevising(false);
                      setNote('');
                      onDone();
                    },
                    onError: report,
                  },
                );
              }}
            >
              {revise.isPending ? 'Sending…' : 'Send request'}
            </Button>
            <Button variant="secondary" onClick={() => setRevising(false)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <div className="supplier-order-actions">
          <Button
            disabled={readOnly || confirm.isPending}
            onClick={() => {
              setError('');
              confirm.mutate(job.order_id, { onSuccess: onDone, onError: report });
            }}
          >
            {confirm.isPending ? 'Accepting…' : 'Accept this order'}
          </Button>
          <Button variant="secondary" disabled={readOnly} onClick={() => setRevising(true)}>
            Ask TAP for a change
          </Button>
        </div>
      )}

      {/* This DOES move the order now — 20261119000100. It used to raise a
          flag TAP read before promoting the job by hand, on the reasoning
          that the shipping decision belonged to the people accountable for
          it. But TAP books no courier: the factory arranges collection, holds
          the tracking number, and was the one party not allowed to say the
          goods had gone. Kyu: "change this to Mark as Shipped."

          Delivery is untouched and stays TAP's: a factory knows when it
          handed the boxes over, not when they arrived. */}
      {!canMarkReady ? null : shipping ? (
        <div className="supplier-ship-form">
          <Input
            label="Tracking number"
            value={tracking}
            placeholder="Paste the courier's number"
            onChange={(event) => setTracking(event.target.value)}
          />
          <Input
            label="Courier"
            value={courier}
            placeholder="DHL, FedEx…"
            onChange={(event) => setCourier(event.target.value)}
          />
          {/* Optional. For DHL, FedEx, UPS and USPS the link is built from the
              number, so this is only needed for a courier we do not
              recognise — which is most local carriers. */}
          <Input
            label="Tracking link (optional)"
            value={trackingUrl}
            placeholder="https://…"
            onChange={(event) => setTrackingUrl(event.target.value)}
          />
          <Textarea
            label="Anything TAP should know (optional)"
            rows={2}
            value={shipNote}
            onChange={(event) => setShipNote(event.target.value)}
          />
          <div className="supplier-order-actions">
            <Button
              disabled={readOnly || shipment.isPending}
              onClick={() => {
                setError('');
                shipment.mutate(
                  {
                    orderId: job.order_id,
                    tracking,
                    courier,
                    ready: true,
                    note: shipNote,
                    url: trackingUrl,
                  },
                  {
                    onSuccess: () => {
                      setShipping(false);
                      setShipNote('');
                      onDone();
                    },
                    onError: report,
                  },
                );
              }}
            >
              {shipment.isPending ? 'Sending…' : 'Mark as Shipped'}
            </Button>
            <Button variant="secondary" onClick={() => setShipping(false)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <div className="supplier-order-actions">
          <Button disabled={readOnly} onClick={() => setShipping(true)}>
            Mark as Shipped &amp; add tracking
          </Button>
        </div>
      )}
    </section>
  );
}
